import {TAG_PRIORITIES, CATEGORY_KEYWORDS, TAG_CONFIG} from '../constants/product-tags'

/**
 * Get the product tag based on categories and promotions
 * @param {Object} product - Product object with categories and promotions
 * @param {Object} tCart - tCart object for translations
 * @returns {Object|null} Tag object with type, text, and color, or null if no tag
 */
export const getProductTag = (product, tCart) => {
    if (!product) return null

    const categories = product?.c_custom?.allCategories || []
    const productPromotions = product.productPromotions || []
    const priceAdjustments = product.priceAdjustments || []

    // Check for promotional tags first (highest priority)
    const hasPromotions = productPromotions.length > 0 || priceAdjustments.length > 0

    if (hasPromotions) {
        // Get promotional message - only from calloutMsg
        let promotionalText = null

        // Only use calloutMsg from productPromotions
        if (productPromotions.length > 0) {
            promotionalText = productPromotions[0].calloutMsg
        }

        // Only show promotional tag if calloutMsg exists
        if (promotionalText) {
            return {
                type: 'promotional',
                text: promotionalText,
                color: TAG_CONFIG.promotional.color,
                priority: TAG_PRIORITIES.promotional
            }
        }
    }

    // Check for category-based tags by priority
    const categoryTags = []

    categories.forEach((category) => {
        const categoryId = category.id?.toLowerCase() || ''

        // Check for each keyword type
        Object.entries(CATEGORY_KEYWORDS).forEach(([tagType, keywords]) => {
            const hasKeyword = keywords.some((keyword) => categoryId.includes(keyword))

            if (hasKeyword) {
                let text = TAG_CONFIG[tagType].text

                if (tCart && text) {
                    switch (tagType) {
                        case 'van-bommel':
                            text = tCart.tagsVanBommel
                            break
                        case 'new':
                            text = tCart.tagsNew
                            break
                        case 'bestseller':
                            text = tCart.tagsBestseller
                            break
                        default:
                            // Keep original text if no translation key matches
                            break
                    }
                }

                categoryTags.push({
                    type: tagType,
                    text: text,
                    color: TAG_CONFIG[tagType].color,
                    priority: TAG_PRIORITIES[tagType]
                })
            }
        })
    })

    // Return the highest priority tag
    if (categoryTags.length > 0) {
        return categoryTags.sort((a, b) => a.priority - b.priority)[0]
    }

    return null
}

/**
 * Check if product has promotions
 * @param {Object} product - Product object
 * @returns {boolean} True if product has promotions
 */
export const hasProductPromotions = (product) => {
    if (!product) return false

    const productPromotions = product.productPromotions || []
    const priceAdjustments = product.priceAdjustments || []

    return productPromotions.length > 0 || priceAdjustments.length > 0
}

/**
 * Get promotion discount percentage
 * @param {Object} product - Product object
 * @returns {number|null} Discount percentage or null
 */
export const getPromotionDiscount = (product) => {
    if (!product) return null

    const priceAdjustments = product.priceAdjustments || []

    if (priceAdjustments.length > 0) {
        const adjustment = priceAdjustments[0]
        return adjustment.appliedDiscount?.percentage || null
    }

    return null
}
